var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { h, Fragment } from 'preact';
import { createPortal } from 'preact/compat';
import { useMemo, useState, useRef, useEffect } from 'preact/hooks';
import { getColourVariable, normalizeShopifyProductGid, parseRelatedColourwaysMetafield, } from '../../utils/relatedColourways';
import { fetchColourwayProductsByIds, } from '../../platforms/shopify/fetchColourwayProducts';
import { addVariantToShopifyThemeCart } from '../../utils/shopifyThemeCart';
import { useAnalytics } from '../../analytics/useAnalytics';
import { useTrackingContext } from '../../analytics/context';
import styles from './MiniCartProductCard.module.scss';
/** Extract the "size" (or equivalent non-colour) option value from a Boost
 *  variant's `merged_options` array, which encodes options as `key:value`
 *  strings (e.g. `"colour:Black"`, `"size:9"`). */
function sizeOption(variant) {
    const parts = variant.merged_options || [];
    for (const option of parts) {
        const [rawKey, rawValue] = option.split(':');
        const key = (rawKey || '').toLowerCase();
        if (key !== 'colour' && key !== 'color' && rawValue)
            return rawValue.trim();
    }
    return null;
}
/** Collect the product's variants for the mini-cart's QUICK ADD toggle. The
 *  label prefers the size/fit option value; when that's absent, falls back
 *  to `variant.title`, then a numeric index, so the pill list always renders
 *  _something_ tappable even for unlabelled variants. */
function collectVariantOptions(product) {
    var _a, _b;
    const raw = (_b = (_a = product.customAttributes) === null || _a === void 0 ? void 0 : _a.boostVariants) !== null && _b !== void 0 ? _b : [];
    const seen = new Set();
    const options = [];
    raw.forEach((variant, index) => {
        var _a, _b;
        if (variant.id == null || seen.has(variant.id))
            return;
        seen.add(variant.id);
        const label = (_b = (_a = sizeOption(variant)) !== null && _a !== void 0 ? _a : variant.title) !== null && _b !== void 0 ? _b : `Option ${index + 1}`;
        options.push({
            id: variant.id,
            label,
            available: variant.available !== false,
        });
    });
    return options;
}
function toImageUrl(img) {
    if (!img)
        return undefined;
    if (typeof img === 'string')
        return img;
    return img.url;
}
function formatPrice(amount) {
    if (amount == null || Number.isNaN(amount))
        return undefined;
    const str = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(amount);
    return `$${str}`;
}
function percentOff(regular, sale) {
    if (!regular || !sale || sale >= regular)
        return null;
    return Math.round(((regular - sale) / regular) * 100);
}
// Simple inline icons — kept local so the component has no asset dependencies.
// `PlusIcon` / `MinusIcon` render at 14×14 inside the 24×24 `.quickAddIcon`
// container (~11-14px visual glyph).
function PlusIcon() {
    return (h("svg", { viewBox: "0 0 14 14", fill: "none", "aria-hidden": "true" },
        h("path", { d: "M7 1v12M1 7h12", stroke: "currentColor", "stroke-width": "1.5", "stroke-linecap": "round" })));
}
function MinusIcon() {
    return (h("svg", { viewBox: "0 0 14 14", fill: "none", "aria-hidden": "true" },
        h("path", { d: "M1 7h12", stroke: "currentColor", "stroke-width": "1.5", "stroke-linecap": "round" })));
}
export function MiniCartProductCard({ product }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const { title, price, image } = product;
    const thumb = toImageUrl(image);
    const analytics = useAnalytics();
    const tracking = useTrackingContext();
    // Related-colourway swatches come from the product's `custom.related_colourways`
    // metafield — same source the full ProductCard reads on the PLP.
    const swatches = useMemo(() => parseRelatedColourwaysMetafield(product), [product]);
    // The product's OWN colour, mirroring ProductCard's `variantSwatchHex`. Aquila
    // ships one colour per product, so this is a single swatch representing the
    // base product. Without it the mini-cart tile shows no colour at all when
    // `related_colourways` is empty — unlike the PLP, which always renders at
    // least this base-colour dot. Hex precedence: explicit `custom.hex_colour`,
    // else map `custom.base_colour` through the theme's `--swatch-*` convention.
    const baseColour = useMemo(() => {
        var _a, _b;
        const metafields = product.metafields;
        const name = (_a = metafields === null || metafields === void 0 ? void 0 : metafields.find((mf) => mf.namespace === 'custom' && mf.key === 'base_colour')) === null || _a === void 0 ? void 0 : _a.value;
        if (!name || !name.trim())
            return null;
        const hex = (_b = metafields === null || metafields === void 0 ? void 0 : metafields.find((mf) => mf.namespace === 'custom' && mf.key === 'hex_colour')) === null || _b === void 0 ? void 0 : _b.value;
        const bg = (hex && hex.trim()) || getColourVariable(name.toLowerCase());
        return { name: name.trim(), bg };
    }, [product]);
    // Currently-selected related-colourway GID (null = base product). Populated
    // by `handleRelatedColourwayClick` — never pre-seeded, so no swatch is
    // selected on mount and no Storefront fetch runs eagerly.
    const [selectedRelatedColourwayGid, setSelectedRelatedColourwayGid] = useState(null);
    // Local per-card cache of Storefront colourway data, keyed by GID. Populated
    // on click via `handleRelatedColourwayClick` → `fetchColourwayProductsByIds`.
    // Kept inside the component (not in a parent / global cache) because the
    // mini-cart tile list is short-lived — it exists only while the cart is
    // open, and survives host changes (portal between desktop side-panel and
    // mobile in-drawer slot) via the same component instance.
    const [colourwayData, setColourwayData] = useState(() => new Map());
    // `fetchingColourwayGid` is set while a fetch is in flight so the UI can
    // optionally show a pending state without stomping on already-fetched data.
    // Null when idle.
    const [fetchingColourwayGid, setFetchingColourwayGid] = useState(null);
    const handleRelatedColourwayClick = (event, swatch) => __awaiter(this, void 0, void 0, function* () {
        event.preventDefault();
        event.stopPropagation();
        const gid = normalizeShopifyProductGid(swatch.id);
        if (!gid)
            return;
        // Always update selection immediately so the swatch visual reacts without
        // waiting on the network — the title / image / variant list will "catch
        // up" when the fetch resolves (or instantly if already cached).
        setSelectedRelatedColourwayGid(gid);
        if (colourwayData.has(gid))
            return;
        setFetchingColourwayGid(gid);
        try {
            const fetched = yield fetchColourwayProductsByIds([gid]);
            setColourwayData(prev => {
                const next = new Map(prev);
                fetched.forEach((value, key) => next.set(key, value));
                return next;
            });
        }
        catch (err) {
            console.error('[MiniCartProductCard] Failed to fetch colourway:', err);
        }
        finally {
            setFetchingColourwayGid(current => (current === gid ? null : current));
        }
    });
    const activeColourway = selectedRelatedColourwayGid
        ? colourwayData.get(selectedRelatedColourwayGid)
        : undefined;
    // Fall back to the swatch's own metafield image / name while the Storefront
    // fetch is in flight — keeps the UI responsive without a blank moment.
    const activeSwatch = selectedRelatedColourwayGid
        ? swatches.find(sw => normalizeShopifyProductGid(sw.id) === selectedRelatedColourwayGid)
        : undefined;
    const displayTitle = (_b = (_a = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.title) !== null && _a !== void 0 ? _a : activeSwatch === null || activeSwatch === void 0 ? void 0 : activeSwatch.name) !== null && _b !== void 0 ? _b : title;
    const displayImage = (_e = (_d = (_c = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.images[0]) === null || _c === void 0 ? void 0 : _c.url) !== null && _d !== void 0 ? _d : activeSwatch === null || activeSwatch === void 0 ? void 0 : activeSwatch.image) !== null && _e !== void 0 ? _e : thumb;
    // Variant options drive the QUICK ADD expand/collapse behaviour below.
    // When a related colourway is selected we swap in the colourway's
    // Storefront size variants; otherwise we use the Boost pass-through.
    const variantOptions = useMemo(() => {
        var _a;
        if ((_a = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.sizeVariants) === null || _a === void 0 ? void 0 : _a.length) {
            return activeColourway.sizeVariants.map(sv => ({
                id: sv.variantId,
                label: sv.size,
                available: sv.available,
            }));
        }
        return collectVariantOptions(product);
    }, [activeColourway, product]);
    const hasMultipleVariants = variantOptions.length > 1;
    const singleVariantId = (_g = (_f = variantOptions[0]) === null || _f === void 0 ? void 0 : _f.id) !== null && _g !== void 0 ? _g : null;
    // Product URL for the thumb/title links. Mirrors ProductCard's `productHref`
    // (handle swaps to the active colourway's), plus a `?variant=` query param
    // so a swatch click carries its selection through to the PDP — otherwise
    // the link always lands on the base product's default variant regardless
    // of which colour/size is showing on the tile. Only appended when there's
    // exactly one resolved variant id (`singleVariantId`); with multiple sizes
    // there's no single "current" variant to link to, so the PDP is left to
    // its own default-variant behaviour for that colourway.
    const productHandle = (_h = activeColourway === null || activeColourway === void 0 ? void 0 : activeColourway.handle) !== null && _h !== void 0 ? _h : product.handle;
    const productHref = `/products/${productHandle}${!hasMultipleVariants && singleVariantId != null ? `?variant=${singleVariantId}` : ''}`;
    // `addingVariantId` doubles as the pending-state flag (null = idle, number =
    // that row is loading). Prevents queued duplicate adds if the network is
    // slow — same pattern as QuickShopButton.
    const [addingVariantId, setAddingVariantId] = useState(null);
    const [variantsExpanded, setVariantsExpanded] = useState(false);
    // Refs on the trigger button + the listbox itself. The listbox is portalled
    // out so it can escape the horizontal-scroll carousel's clip box (`.list` on
    // mobile has `overflow-x: auto` which implicitly clips vertical overflow
    // too). Because the listbox no longer lives inside the card's DOM, the
    // outside-click check has to consult both refs.
    //
    // Portal target: prefer `<cart-drawer>` over `document.body`. The Aquila
    // theme's mobile cart styling (in `main.css`) zeroes the opacity of every
    // direct body child except `<cart-drawer>` while the cart is open
    // (`body.page-overlay-cart-on > * { opacity: 0 }`); a body-portalled
    // listbox would get caught by that rule and become invisible. Cart-drawer
    // has no transform / filter so `position: fixed` on the listbox still
    // resolves against the viewport, and there's no stacking-context conflict.
    // Falls back to body when no cart-drawer is on the page (e.g. dev harness).
    const triggerRef = useRef(null);
    const listboxRef = useRef(null);
    const portalTarget = useMemo(() => {
        var _a;
        return (typeof document !== 'undefined'
            ? ((_a = document.querySelector('cart-drawer')) !== null && _a !== void 0 ? _a : document.body)
            : null);
    }, []);
    // Close the dropdown when the shopper clicks outside it or presses Escape —
    // same UX contract as SortDropdown. Listeners are only registered while the
    // dropdown is actually open so we don't pay for them on every idle card.
    useEffect(() => {
        if (!variantsExpanded)
            return;
        const isInsideDropdown = (target) => {
            var _a, _b, _c, _d;
            if (!target)
                return false;
            return ((_b = (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)) !== null && _b !== void 0 ? _b : false)
                || ((_d = (_c = listboxRef.current) === null || _c === void 0 ? void 0 : _c.contains(target)) !== null && _d !== void 0 ? _d : false);
        };
        const handleOutsideClick = (event) => {
            if (!isInsideDropdown(event.target))
                setVariantsExpanded(false);
        };
        const handleEscape = (event) => {
            if (event.key === 'Escape')
                setVariantsExpanded(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [variantsExpanded]);
    // Floating-dropdown coordinates (fixed positioning relative to viewport).
    // Recomputed on open, resize, and capture-phase scroll so the listbox tracks
    // the trigger if the cart body scrolls while the dropdown is open.
    //
    // Flip placement: if there's not enough room below the trigger for the
    // preferred dropdown height, render it ABOVE the trigger instead (standard
    // Popper / Floating UI pattern, also how native <select> behaves). `maxHeight`
    // is capped to whichever side we chose so the listbox never overflows the
    // viewport — shoppers get an internal scrollbar in the worst case instead
    // of content hidden past the bottom edge.
    const [dropdownPos, setDropdownPos] = useState(null);
    useEffect(() => {
        if (!variantsExpanded) {
            setDropdownPos(null);
            return;
        }
        const update = () => {
            const btn = triggerRef.current;
            if (!btn)
                return;
            const rect = btn.getBoundingClientRect();
            const gap = 4;
            const preferredHeight = 160;
            const spaceBelow = window.innerHeight - rect.bottom - gap;
            const spaceAbove = rect.top - gap;
            // Prefer below. Flip above when below can't fit the preferred height
            // and above has more room. `maxHeight` clamps the listbox so it
            // always fits inside the viewport on the chosen side.
            const placeBelow = spaceBelow >= preferredHeight || spaceBelow >= spaceAbove;
            const maxHeight = Math.max(0, Math.min(preferredHeight, placeBelow ? spaceBelow : spaceAbove));
            const top = placeBelow ? rect.bottom + gap : rect.top - maxHeight - gap;
            // Match the QUICK ADD button's width exactly — the dropdown tracks the
            // trigger's width so short labels give a compact popover that doesn't
            // overhang the tile.
            setDropdownPos({ top, left: rect.left, minWidth: rect.width, maxHeight });
        };
        update();
        window.addEventListener('resize', update);
        // `capture: true` so we catch scroll events on ancestors too (the cart
        // drawer body + the horizontal carousel both scroll independently).
        window.addEventListener('scroll', update, { capture: true, passive: true });
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, { capture: true });
        };
    }, [variantsExpanded]);
    const addVariant = (variantId) => __awaiter(this, void 0, void 0, function* () {
        if (addingVariantId !== null)
            return;
        setAddingVariantId(variantId);
        try {
            yield addVariantToShopifyThemeCart(variantId);
            // Fire Boost's add_to_cart for this recommended tile. No-op without a
            // TrackingProvider (tests / standalone harness). itemListId is
            // 'recommendations' and correlationId is the recs rid — so the add is
            // attributed to the recommendation, mirroring ProductCard.handleAddedToCart.
            if (tracking) {
                analytics.trackAddToCart({
                    itemId: String(product.id),
                    itemVariantId: String(variantId),
                    itemListId: tracking.itemListId,
                    pageType: tracking.pageType,
                    correlationId: tracking.correlationId,
                    widgetId: tracking.widgetId,
                });
            }
            // Close the size picker on success — the cart drawer's own re-render
            // (triggered by `addVariantToShopifyThemeCart` via `cart:updated`) will
            // surface the newly-added line item, so leaving the dropdown open is
            // noise. On error we deliberately keep it open so the shopper can pick a
            // different size or retry without re-toggling.
            setVariantsExpanded(false);
        }
        catch (err) {
            console.error('[MiniCartProductCard] Add to cart error:', err);
        }
        finally {
            setAddingVariantId(null);
        }
    });
    const handleQuickAddToggle = () => {
        if (hasMultipleVariants) {
            // Toggle the inline option list; the flipped icon signals the state.
            setVariantsExpanded(e => !e);
            return;
        }
        if (singleVariantId != null)
            addVariant(singleVariantId);
    };
    const regular = (_j = price === null || price === void 0 ? void 0 : price.regular) !== null && _j !== void 0 ? _j : 0;
    const sale = price === null || price === void 0 ? void 0 : price.sale;
    const isOnSale = typeof sale === 'number' && sale > 0 && sale < regular;
    const discount = isOnSale ? percentOff(regular, sale) : null;
    const isAddingAny = addingVariantId !== null;
    const canAdd = variantOptions.length > 0;
    const quickAddLabel = isAddingAny && !hasMultipleVariants ? 'Adding…' : 'Quick add';
    return (h("article", { class: styles.card },
        h("a", { href: productHref, class: styles.thumb }, displayImage && (
        // `key={displayImage}` forces Preact to unmount the old <img> when
        // the src changes (e.g. swatch click) instead of keeping the stale
        // image visible while the new URL loads in-place. The thumb's
        // off-white background shows through during the brief load window.
        h("img", { key: displayImage, class: styles.thumbImg, src: displayImage, alt: displayTitle, loading: "lazy" }))),
        h("div", { class: styles.body },
            h("div", { class: styles.titleGroup },
                h("a", { href: productHref, class: styles.title }, displayTitle),
                h("div", { class: styles.priceRow }, isOnSale ? (h(Fragment, null,
                    h("span", { class: `${styles.price} ${styles.priceSale}` }, formatPrice(sale)),
                    h("span", { class: styles.priceCompare }, formatPrice(regular)),
                    discount != null && h("span", { class: styles.pill },
                        discount,
                        "% OFF"))) : (h("span", { class: styles.price }, formatPrice(regular))))),
            (baseColour || swatches.length > 0) && (h("div", { class: styles.swatchContainer, role: "group", "aria-label": "Colour options" },
                baseColour && (
                // The product's own colour. Selected when no related colourway is
                // active; clicking it returns the tile to the base product.
                h("button", { key: "__base", type: "button", class: `${styles.swatch} ${selectedRelatedColourwayGid === null ? styles.swatchSelected : styles.swatchUnselected}`, onClick: () => setSelectedRelatedColourwayGid(null), "aria-pressed": selectedRelatedColourwayGid === null, "aria-label": baseColour.name, title: baseColour.name },
                    h("span", { class: styles.swatchInner, style: { backgroundColor: baseColour.bg } }))),
                swatches.map(sw => {
                    var _a, _b, _c;
                    const gid = normalizeShopifyProductGid(sw.id);
                    const isSelected = gid != null && gid === selectedRelatedColourwayGid;
                    // Mirrors `bgHex` in ProductCard.tsx: prefer the swatch's own
                    // `styleColourHex` from the metafield, otherwise resolve the
                    // base colour name through the theme's `--swatch-*` CSS
                    // custom-property convention via `getColourVariable`.
                    const bg = (sw.styleColourHex && sw.styleColourHex.trim()) ||
                        getColourVariable(((_a = sw === null || sw === void 0 ? void 0 : sw.baseColour) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || 'grey');
                    return (h("button", { key: String((_c = (_b = sw.id) !== null && _b !== void 0 ? _b : sw.name) !== null && _c !== void 0 ? _c : sw.styleColour), type: "button", class: `${styles.swatch} ${isSelected ? styles.swatchSelected : styles.swatchUnselected}`, onClick: (event) => handleRelatedColourwayClick(event, sw), "aria-pressed": isSelected, "aria-label": sw.name || String(sw.styleColour || 'colour'), title: sw.name || String(sw.styleColour || '') },
                        h("span", { class: styles.swatchInner, style: { backgroundColor: bg } })));
                }))),
            h("div", { class: styles.quickAddRow },
                h("button", { ref: triggerRef, type: "button", class: styles.quickAdd, onClick: handleQuickAddToggle, disabled: !canAdd || (isAddingAny && !hasMultipleVariants), "aria-label": hasMultipleVariants
                        ? `Choose a size for ${displayTitle}`
                        : `Quick add ${displayTitle}`, "aria-haspopup": hasMultipleVariants ? 'listbox' : undefined, "aria-expanded": hasMultipleVariants ? variantsExpanded : undefined },
                    h("span", null, quickAddLabel),
                    h("span", { class: styles.quickAddIcon }, hasMultipleVariants && variantsExpanded ? h(MinusIcon, null) : h(PlusIcon, null))),
                hasMultipleVariants && variantsExpanded && dropdownPos && portalTarget && createPortal(h("ul", { ref: listboxRef, class: styles.variantOptionsList, role: "listbox", "aria-label": "Available options", style: {
                        top: `${dropdownPos.top}px`,
                        left: `${dropdownPos.left}px`,
                        minWidth: `${dropdownPos.minWidth}px`,
                        maxHeight: `${dropdownPos.maxHeight}px`,
                    } }, variantOptions.map(opt => {
                    const adding = addingVariantId === opt.id;
                    const disabled = !opt.available || (isAddingAny && !adding);
                    const rowClass = adding
                        ? styles.variantOptionAdding
                        : (disabled ? styles.variantOptionDisabled : styles.variantOption);
                    return (h("li", { key: opt.id, class: rowClass, onClick: () => { if (!disabled)
                            addVariant(opt.id); }, role: "option", "aria-selected": adding, "aria-disabled": disabled }, adding ? 'Adding…' : opt.label));
                })), portalTarget)))));
}
// Skeleton variant mirroring the same layout, used by MiniCartRecommendationWidget
// while the recs API is pending.
export function MiniCartProductCardSkeleton() {
    return (h("article", { class: styles.card, "aria-hidden": "true" },
        h("div", { class: styles.skeletonThumb }),
        h("div", { class: styles.body },
            h("div", { class: styles.titleGroup },
                h("div", { class: `${styles.skeletonLine} ${styles.skeletonLineTitle}` }),
                h("div", { class: `${styles.skeletonLine} ${styles.skeletonLinePrice}` })),
            h("div", { class: `${styles.skeletonLine} ${styles.skeletonLineButton}` }))));
}
